/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { makeAutoObservable } from "mobx"

import type { Organization, SelectedOrgId } from "./organization-types"

export class OrganizationStore {
    organizations: Map<number, Organization> = new Map()

    // Set once the first full list has loaded, so loading can be distinguished from an empty list.
    organizationsLoaded = false

    // Selected none organization by default
    selectedOrgId: SelectedOrgId = "none"

    constructor() {
        makeAutoObservable(this, {
            setOrganizations: true,
            setSelectedOrgId: true,
            setSelectedOrg: true,
            appendOrganization: true,
            updateOrganization: true,
            removeOrganization: true
        })
    }

    get organizationsArray(): Organization[] {
        return Array.from(this.organizations.values())
    }

    get organizationsIds(): number[] {
        return Array.from(this.organizations.keys())
    }

    get selectedOrganization(): Organization | null {
        return this.selectedOrgId === "none" ? null : (this.organizations.get(this.selectedOrgId) ?? null)
    }

    setOrganizations(orgs: Organization[]): void {
        this.organizations = new Map(orgs.map(keyVal))
        this.organizationsLoaded = true
    }

    setSelectedOrgId(orgId: SelectedOrgId): void {
        this.selectedOrgId = orgId
    }

    setSelectedOrg(org: Organization): void {
        this.organizations.set(org.id, org)
        this.setSelectedOrgId(org.id)
    }

    appendOrganization(org: Organization): void {
        this.organizations.set(org.id, org)
    }

    updateOrganization(org: Organization): void {
        this.organizations.set(org.id, org)
    }

    removeOrganization(orgId: number): void {
        this.organizations.delete(orgId)
    }

    // At the time of calling selectedOrganizationName, we should have selectedOrganization set.
    get selectedOrgName(): string {
        return this.selectedOrganization?.name ?? ""
    }
}

function keyVal(org: Organization): [number, Organization] {
    return [org.id, org]
}
